import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';

import AppCustomization from '../../../CoachTools/Model/AppCustomization';
import Coach, { ColorModes } from '../../../Model/Coach';
import UserContract from '../../../Model/UserContract';
import { buildShareCard } from '../core/shareCard';

const ShareCardStatus = {
  LOADING: 'loading',
  READY: 'ready',
  UNAVAILABLE: 'unavailable',
};

/*
  Looks the challenge up by its id, through its BASE contract: the challenge's public record, the one the coach
  turns sharing on for. A client's own contract (which holds their name and email) is never read, so a
  challenge id is safe to put in a link.
*/
const loadShareCard = async (challengeId) => {
  const contract = await UserContract.getBaseContractByChallengeId(challengeId);
  if (!contract?.isStoryShareEnabled) {
    return null;
  }
  const [coach, appCustomization] = await Promise.all([
    Coach.getCoachById(contract.coach),
    AppCustomization.getAppCustomizationByCoach(contract.coach),
  ]);
  return buildShareCard({
    contract,
    coach,
    appCustomization,
    isDarkTheme: coach?.checkoutPageColorMode !== ColorModes.LIGHT,
  });
};

/**
 * The commitment sticker's card for a challenge, when the coach turned story sharing on for it.
 *
 * @param {?string} challengeId
 * @returns {{ status: string, card: ?import('../core/shareCard').ShareCard }} status is a ShareCardStatus.
 *   UNAVAILABLE covers no challenge, sharing off and a failed load, so callers just show their usual page. A
 *   challenge id that arrives after the first render reads as LOADING too, until its card is in.
 */
const useShareCard = (challengeId) => {
  // Keyed on the challenge it was loaded for, so a page never shows one challenge's result for another.
  const [state, setState] = useState({ challengeId: null, status: ShareCardStatus.UNAVAILABLE, card: null });

  useEffect(() => {
    if (!challengeId) {
      return undefined;
    }
    let isCurrent = true;
    loadShareCard(challengeId)
      .catch((error) => {
        Sentry.captureException(error, { extra: { challengeId } });
        return null;
      })
      .then((card) => {
        if (isCurrent) {
          setState({ challengeId, status: card ? ShareCardStatus.READY : ShareCardStatus.UNAVAILABLE, card });
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [challengeId]);

  if (!challengeId) {
    return { status: ShareCardStatus.UNAVAILABLE, card: null };
  }
  if (state.challengeId !== challengeId) {
    return { status: ShareCardStatus.LOADING, card: null };
  }
  return { status: state.status, card: state.card };
};

export { ShareCardStatus };
export default useShareCard;
